/**
 * Подключение legacy-CSS страницы «как в шаблоне»: admin.tpl тянет свой набор
 * <link rel=stylesheet>, а отдельные шаблоны докидывают свои ({$data.css}).
 *
 * Хук вешает <link> в document.head на время жизни компонента и снимает при
 * размонтировании. Счётчик ссылок — чтобы два компонента, просящих один и тот же
 * файл (AdminLayout + страница), не гасили его друг другу.
 */

import { useEffect } from 'react'

const refs = new Map<string, number>()
const nodes = new Map<string, HTMLLinkElement>()

export function useStylesheets(hrefs: readonly string[]): void {
  const key = hrefs.join('|')

  useEffect(() => {
    const list = key === '' ? [] : key.split('|')

    for (const href of list) {
      const count = (refs.get(href) ?? 0) + 1
      refs.set(href, count)
      if (count === 1) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = href
        document.head.appendChild(link)
        nodes.set(href, link)
      }
    }

    return () => {
      for (const href of list) {
        const count = (refs.get(href) ?? 1) - 1
        if (count <= 0) {
          refs.delete(href)
          nodes.get(href)?.remove()
          nodes.delete(href)
        } else {
          refs.set(href, count)
        }
      }
    }
  }, [key])
}

/**
 * Отключить стили публичной части на время жизни компонента (для админки).
 *
 * В legacy admin.tpl НЕ подключает main.css, а в нём переопределены
 * .invoicessheadblock и .historyheadblock (лишний margin) — из-за чего блоки
 * «Выставленные квитанции» и «История по счету» в карточке участка съезжали вправо.
 * Глобальный <link data-scope="public"> в index.html нужен, чтобы публичные страницы
 * не мигали нестилизованными; здесь он временно гасится через link.disabled.
 */
export function useDisablePublicCss(): void {
  useEffect(() => {
    // link.disabled = true оказался ненадёжен (лист остаётся активным и стили
    // продолжают применяться), поэтому тег временно вынимается из DOM.
    const links = [...document.querySelectorAll<HTMLLinkElement>('link[data-scope="public"]')]
    const anchors = links.map((link) => ({ link, parent: link.parentNode, next: link.nextSibling }))
    for (const { link } of anchors) link.remove()

    return () => {
      for (const { link, parent, next } of anchors) parent?.insertBefore(link, next)
    }
  }, [])
}

/** Набор стилей админки — точный список из htdocs/core/templates/admin.tpl. */
export const ADMIN_CSS: readonly string[] = [
  'https://fonts.googleapis.com/css?family=PT+Sans+Narrow',
  '/assets/css/bootstrap.min.css',
  '/assets/css/bootstrap-select.min.css',
  '/assets/css/docs.min.css',
  '/assets/css/admin.css',
  '/assets/css/all.css',
  '/assets/css/summernote.css',
  '/assets/css/newapproach.css',
  '/assets/css/jExplorer.css',
]
